import { Fragment, useMemo } from 'react';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { getCommercialFlightInfoString } from '@lp/features/RoutePlanner/components/RouteLegForm/components/SearchRecommendedFlights/utils';
import { parseAbsolute } from '@internationalized/date';
import { AirportRouteLocation, CommercialFlightAviaLeg, RouteLegTypeEnum, RouteTypeEnum } from '@/api/apis/route';
import { convertMsToTimeString, getTimeDifference } from '@/utils/helpers';

type FlightStopsTooltipProps = {
  routeType: RouteTypeEnum;
  sabreGroupId: string;
};

export const FlightStopsTooltip = ({ routeType, sabreGroupId }: FlightStopsTooltipProps) => {
  const { list } = useRouteLocations();
  const { legs } = useRouteDetails(routeType);

  const legsList = useMemo(() => {
    return legs.filter(
      (leg): leg is CommercialFlightAviaLeg =>
        leg.legType === RouteLegTypeEnum.CommercialFlightAviaLeg && leg.sabreGroupId === sabreGroupId,
    );
  }, [legs, sabreGroupId]);

  return (
    <div className="w-full flex flex-col gap-4 px-2 py-1">
      {legsList.map((leg, index) => {
        const departureAirportInfo = list.find(
          (airport) => airport.id === leg.departureLocation.locationId,
        ) as AirportRouteLocation;
        const arrivalAirportInfo = list.find(
          (airport) => airport.id === leg.arrivalLocation.locationId,
        ) as AirportRouteLocation;
        const prevArrivalTime = legsList[index - 1]
          ? parseAbsolute(legsList[index - 1].arrivalTime, legsList[index - 1].arrivalLocalTimezone)
          : null;
        const legDuration = getTimeDifference(new Date(leg.departureTime), new Date(leg.arrivalTime));
        const stopDuration = prevArrivalTime
          ? getTimeDifference(new Date(leg.departureTime), new Date(prevArrivalTime.toAbsoluteString()))
          : null;
        const flightInfo = [leg.flightOperator, leg.flightNumber, leg.seatType].filter((i) => i).join(' | ');

        return (
          <Fragment key={leg.departureTime}>
            {!!stopDuration && (
              <span className="text-m-sb text-black">{`1 stop ${convertMsToTimeString(stopDuration.MILLISECONDS)} in ${departureAirportInfo.iata}`}</span>
            )}
            <div className="flex flex-col">
              <span className="text-m-sb text-black">{convertMsToTimeString(legDuration.MILLISECONDS)}</span>
              <span className="text-m-r text-black">
                {getCommercialFlightInfoString(leg.departureTime, leg.departureLocalTimezone, departureAirportInfo)}
              </span>
              <span className="text-m-r text-black">
                {getCommercialFlightInfoString(leg.arrivalTime, leg.arrivalLocalTimezone, arrivalAirportInfo)}
              </span>
              {!!flightInfo && <span className="text-m-r text-black">{flightInfo}</span>}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
};
